import { useMemo, useState } from 'react';
import { useCosts } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { CostRow } from '@/api/types';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Badge } from '@/components/arc/badge/badge';
import { Button } from '@/components/arc/button/button';
import { DonutChart } from '@/components/arc/donut-chart/donut-chart';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { LineChart, type LineChartDatum, type LineChartSeries } from '@/components/arc/line-chart/line-chart';
import { MetricCard } from '@/components/arc/metric-card/metric-card';
import SegmentedControl from '@/components/arc/segmented-control/segmented-control';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { fmtMoney, fmtNumber, labelize } from '@/lib/format';
import { KpiCard } from './KpiCard';
import { axisLabelAt, fillPeriods, periodAxisLabel, periodLabel } from './chartFormat';
import styles from './costs.module.css';

type LedgerRow = { [K in keyof CostRow]: CostRow[K] } & { id: string };
type Granularity = 'day' | 'month';

const GRANULARITIES = [
  { value: 'day', label: 'Daily' },
  { value: 'month', label: 'Monthly' },
];
const RANGES = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
];

export function CostsPage() {
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [days, setDays] = useState('30');
  const costs = useCosts(granularity, Number(days));

  const { data, series, byModel, ledger, hasStatus } = useMemo(() => {
    const rows = costs.data?.rows ?? [];
    const models = [...new Set(rows.map((r) => r.model))];
    // Every period of the range is a point, so days without spend sit at 0 instead of being skipped.
    const periods = [...new Set([...fillPeriods(granularity, Number(days), new Date()), ...rows.map((r) => r.period)])].sort();
    const perPeriod = new Map<string, Record<string, number>>();
    const totals = new Map<string, number>();
    for (const r of rows) {
      const bucket = perPeriod.get(r.period) ?? {};
      bucket[r.model] = (bucket[r.model] ?? 0) + r.cost_usd;
      perPeriod.set(r.period, bucket);
      totals.set(r.model, (totals.get(r.model) ?? 0) + r.cost_usd);
    }
    const zero = Object.fromEntries(models.map((m) => [m, 0]));
    const data: LineChartDatum[] = periods.map((p, i) => ({
      key: p,
      label: periodLabel(p),
      axisLabel: axisLabelAt(i, periods.length) ? periodAxisLabel(p) : undefined,
      values: { ...zero, ...perPeriod.get(p) },
    }));
    const series: LineChartSeries[] = models.map((m, i) => ({ key: m, label: m, area: i === 0 }));
    const byModel = models.map((m) => ({ key: m, label: m, value: totals.get(m) ?? 0 }));
    // Every row the API returns is listed, including failed attempts with no cost once the API reports them.
    const ledger: LedgerRow[] = rows.map((r, i) => ({ ...r, id: `${r.period}:${r.model}:${r.status ?? ''}:${i}` }));
    const hasStatus = rows.some((r) => r.status);
    return { data, series, byModel, ledger, hasStatus };
  }, [costs.data, granularity, days]);

  const columns: DataColumn<LedgerRow>[] = [
    { key: 'period', label: 'Period', render: (v) => periodLabel(String(v)) },
    { key: 'model', label: 'Model', render: (v) => <code className={styles.mono}>{String(v)}</code> },
    ...(hasStatus
      ? [
          {
            key: 'status',
            label: 'Status',
            render: (v: unknown) => (
              <Badge size="sm" tone={v === 'failed' ? 'danger' : v === 'skipped' ? 'warning' : 'success'}>
                {labelize(v ? String(v) : 'done')}
              </Badge>
            ),
          } satisfies DataColumn<LedgerRow>,
        ]
      : []),
    { key: 'videos', label: 'Videos', numeric: true, render: (v) => fmtNumber(Number(v)) },
    { key: 'tokens_in', label: 'Tokens in', numeric: true, render: (v) => fmtNumber(Number(v)) },
    { key: 'tokens_out', label: 'Tokens out', numeric: true, render: (v) => fmtNumber(Number(v)) },
    { key: 'cost_usd', label: 'Cost', numeric: true, render: (v) => fmtMoney(Number(v), 4) },
  ];

  const summary = costs.data;

  return (
    <main className="page">
      <PageHeading
        title="Costs"
        description="Model spend from the cost ledger, priced per token"
        actions={
          <>
            <SegmentedControl
              label="Granularity"
              options={GRANULARITIES}
              value={granularity}
              onValueChange={(v) => setGranularity(v as Granularity)}
            />
            <SegmentedControl label="Range" options={RANGES} value={days} onValueChange={setDays} />
          </>
        }
      />

      {costs.isError ? (
        <Alert tone="danger" title="Costs did not load">
          {errorMessage(costs.error)}{' '}
          <Button variant="secondary" size="sm" onClick={() => void costs.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : !summary ? (
        <Skeleton lines={6} label="Loading costs" />
      ) : (
        <>
          <div className="kpi-grid" aria-busy={costs.isFetching}>
            <KpiCard
              label="Total spend"
              value={summary.total_cost_usd}
              prefix="$"
              decimals={2}
              context={`Last ${days} days`}
            />
            <MetricCard label="Videos analyzed" value={summary.total_videos} context={`Last ${days} days`} />
            <KpiCard
              label="Average per video"
              value={summary.avg_cost_per_video_usd}
              prefix="$"
              decimals={4}
              context="Across analyzed videos"
            />
            <KpiCard
              label="Projected this month"
              value={summary.projected_month_usd}
              prefix="$"
              decimals={2}
              context="From the month-to-date daily average"
            />
          </div>

          {ledger.length === 0 ? (
            <EmptyState
              title="No spend in this range"
              description="Costs appear here once a video analysis finishes. Try a longer range."
              action={
                days !== '365' ? (
                  <Button variant="secondary" onClick={() => setDays('365')}>
                    Show the last year
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className={styles.charts}>
                <section className="stack-4" aria-labelledby="spend-heading">
                  <h2 id="spend-heading" className={styles.sectionTitle}>
                    Spend by model
                  </h2>
                  <LineChart
                    data={data}
                    series={series}
                    label="Spend by model over time"
                    height={260}
                    formatValue={(v) => fmtMoney(v, 4)}
                    formatTick={(v) => `$${v.toFixed(2)}`}
                    loading={costs.isFetching}
                    categoryLabel={granularity === 'day' ? 'Day' : 'Month'}
                  />
                </section>
                <section className="stack-4" aria-labelledby="share-heading">
                  <h2 id="share-heading" className={styles.sectionTitle}>
                    Share by model
                  </h2>
                  <DonutChart
                    data={byModel}
                    label="Share of spend by model"
                    formatValue={(v) => fmtMoney(v, 2)}
                    totalLabel="Total spend"
                    size={200}
                  />
                </section>
              </div>

              <section className="stack-4" aria-labelledby="ledger-heading">
                <h2 id="ledger-heading" className={styles.sectionTitle}>
                  Ledger
                </h2>
                <div className="table-scroll">
                  <SortableDataTable<LedgerRow>
                    rows={ledger}
                    columns={columns}
                    rowKey="id"
                    caption="Cost ledger"
                    defaultSort={{ key: 'period', direction: 'desc' }}
                    itemName={{ one: 'entry', other: 'entries' }}
                  />
                </div>
              </section>
            </>
          )}
        </>
      )}
    </main>
  );
}
